import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeAllEntities, subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { CrDataWarning } from '@chancenwerk/client/components/chancen-component-data-warning.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { TeamMeeting } from '@chancenwerk/shared/attendance/entities/TeamMeeting.js';
import { TeamMeetingAttendance } from '@chancenwerk/shared/attendance/entities/TeamMeetingAttendance.js';
import '@chancenwerk/client/components/semantics/chancen-component-semantic-kpf-picker.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { ChancenRole } from '@chancenwerk/shared/semantics/ChancenRole.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import '@chancenwerk/client/design/components/cr-textarea.js';
import { KPFAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { switchMap, tap } from 'rxjs';
import '../components/board-component-course-digital-card.js';

/**
 * @customElement board-page-team-treffen
 */
@customElement('board-page-team-treffen')
export class BoardPageTeamTreffenOverview extends CrPage {
  @property() tid = new RXController(this, '');

  @state() _meeting = new RXController(
    this,
    this.tid.observable.pipe(
      switchMap(id => {
        return subscribeByID(
          TeamMeeting,
          id,
        )({
          when: 1,
          until: 1,
          comment: 1,
          attendances: { userID: 1 },
          kpfId: 1,
          kpf: { kuerzel: 1, produkt: { kuerzel: 1, name: 1 }, school: { kuerzel: 1, name: 1 } },
        });
      }),
      tap(meeting => {
        this.label = `Team Treffen: ${meeting?.when?.toFormat('dd.MM.yyyy HH:mm')} - ${meeting?.until?.toFormat(
          'HH:mm',
        )}`;
        this.description = `${meeting?.kpf?.school?.name} ${meeting?.kpf?.kuerzel ?? ''}`;
      }),
    ),
  );

  @state() _roles = new RXController(this, subscribeAllEntities(ChancenRole)({ _id: 1, name: 1, readableName: 1 }));

  constructor() {
    super();
    this.label = 'Team Treffen';
    this.permission = ChancenPermission.board.EDIT_TEAM_TREFFEN;
    this.backRoute = '/team-treffen';
  }

  override header() {
    return html`<cr-flex horizontal crossaxis-center space="sm">
      ${CrDataWarning(this._meeting.value?.isDirty())}
      <chancen-component-context-menu
        .items=${[
          {
            icon: 'delete',
            label: 'Löschen',
            action: async () => {
              ChromiumDialog.guard(
                async () => {
                  await this._meeting.value?.remove();
                  goTo('/team-treffen');
                },
                { hasToConfirm: true, confirmText: 'Wirklich löschen?' },
              );
            },
          },
        ]}
      >
        Inhalt Hinzufügen
      </chancen-component-context-menu>
      <cr-button
        @click=${async () => {
          ChromiumDialog.guard(async () => {
            await this._meeting.value?.save();
          });
        }}
        >Speichern</cr-button
      >
    </cr-flex>`;
  }

  override content() {
    const options = buildChancenDataViewOptions<typeof Mitarbeiter>(
      {
        class: Mitarbeiter,
        operation: (skip, limit, filter) => {
          const finalFilter = new ChancenwerkUserFilter({
            userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
            aspects: [
              new BooleanAspect({ fieldName: 'guestAccount', value: false }),
              new KPFAspect({ value: this._meeting.value?.kpfId }),
              new BooleanAspect({ customID: 'active', value: true }),
              ...filter.aspects,
            ],
          });

          return UserAPI.subscribeByFilter(skip, limit, finalFilter);
        },
        view: new View({ filter: new ChancenwerkUserFilter({}) }),
        isColumnResortable: false,
        isEntryClickable: false,
        customColumns: [
          {
            customID: 'Anwesend',
            columnName: 'Anwesend',
            columnWidth: 'max-content',
            isSortable: false,
            getFieldMapping: ent => {
              const exists = this._meeting.value?.attendances?.some(a => a.userID === ent._id);
              return html`<cr-flex ${css({ alignItems: 'end' })}>
                <cr-checkbox
                  .value=${exists}
                  @value-picked=${(e: ValueEvent<boolean>) => {
                    if (!this._meeting.value) {
                      return;
                    }

                    if (e.detail.value) {
                      this._meeting.value.attendances?.push(new TeamMeetingAttendance({ userID: ent._id }));
                    } else {
                      this._meeting.value.attendances = this._meeting.value?.attendances?.filter(
                        a => a.userID !== ent._id,
                      );
                    }

                    this.requestUpdate();
                  }}
                ></cr-checkbox>
              </cr-flex>`;
            },
          },
          {
            fieldName: 'Name',
            columnName: 'Name',
            isSortable: false,
            selectionSet: { vorname: 1, nachname: 1, email: 1, roles: { name: 1 } },
            getFieldMapping: ent =>
              html`<cr-flex space="sm">
                <cr-text bold>${ent.toString()}</cr-text>
                <cr-text ${css({ opacity: 0.7 })}>
                  ${ent.roles?.map(r => this._roles.value?.find(cr => cr.name === r.name)?.readableName).join(', ')}
                </cr-text>
              </cr-flex> `,
          },
        ],
      } as DataViewOptions<typeof Mitarbeiter>,
      { fieldAspectGeneration: { hideFieldsWithoutTranslation: true, whitelist: { vorname: 1, nachname: 1 } } },
    );

    return guard(
      [this._meeting.value?.kpfId],
      () => html`
        <cr-flex padding="md" space="md">
          <cr-card padding="md">
            <cr-textarea
              placeholder="Kommentar"
              .value=${this._meeting.value?.comment}
              @value-picked=${(e: ValueEvent<string>) => {
                if (!this._meeting.value) {
                  return;
                }
                this._meeting.value.comment = e.detail.value;
              }}
            ></cr-textarea>
          </cr-card>
          <cr-card padding="sm"> ${FilterEditor({ options: options.filterOptions })} </cr-card>
          <cr-data-view .options=${options.dataViewOptions}> </cr-data-view>
        </cr-flex>
      `,
    );
  }
}
